import { Redirect, useLocation } from 'wouter';
import { StatusMessage } from '../components/StatusMessage.tsx';
import { useSession } from './session.ts';
import type { ReactNode } from 'react';

interface SessionGateProps {
  readonly children: ReactNode;
}

/** Where to send an anonymous visitor so the login page can bring them back. */
export function loginPathFor(currentPath: string): string {
  return currentPath === '/' ? '/login' : `/login?next=${encodeURIComponent(currentPath)}`;
}

/**
 * Decides whether the visitor has a session before any of the authenticated
 * bundle is requested. Cold loads (and Lighthouse) start at "/": an anonymous
 * visitor must reach the login form after one script download, not after a
 * second chunk that exists only to send them away again.
 *
 * Only the first answer gates the app. The session route is never kept for
 * offline use, so a refetch of it with no signal (every write refetches
 * every query) fails; the last answer stands until a refetch succeeds,
 * rather than the whole app vanishing behind an error mid-edit. A session
 * that really ended is reported by the protected route that saw the 401,
 * which flips the cached answer to signed out.
 */
export function SessionGate({ children }: SessionGateProps) {
  const session = useSession();
  const [location] = useLocation();

  if (session.data === undefined) {
    if (session.isError) {
      return (
        <StatusMessage tone="error">
          Could not reach the server to check your session. Reload to try again.
        </StatusMessage>
      );
    }
    return <StatusMessage>Checking your session…</StatusMessage>;
  }
  if (!session.data.authenticated) {
    return <Redirect to={loginPathFor(location)} replace />;
  }
  return children;
}
